import React, { useEffect, useState } from "react";
import { EthernetPort, Activity, ArrowDown, ArrowUp, AlertCircle } from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";

interface SnmpInterfaceProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

interface InterfaceData {
    id: string;
    index: number;
    description: string;
    status: string; // "up" | "down"
    admin_status: string;
    mac_address: string;
    speed: string;
    in_octets: number;
    out_octets: number;
    in_errors: number;
    out_errors: number;
    // add other fields if necessary
}

const SnmpInterface: React.FC<SnmpInterfaceProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [portsData, setPortsData] = useState<InterfaceData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedPort, setSelectedPort] = useState<number | null>(null);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id,
                })
            };
            const response = await baseapi.get('/snmpinterfaces', { params });
            if (response.data.status) {
                // Sort by index to ensure visual grid looks correct
                const sortedData = (response.data.data || []).sort((a: InterfaceData, b: InterfaceData) => a.index - b.index);
                setPortsData(sortedData);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, []);

    // --- Visualization Logic ---

    // Calculate Stats
    const upCount = portsData.filter((p) => p.status === 'up').length;
    const downCount = portsData.length - upCount;
    const totalOctetsIn = portsData.reduce((acc, curr) => acc + (curr.in_octets || 0), 0);
    const totalOctetsOut = portsData.reduce((acc, curr) => acc + (curr.out_octets || 0), 0);

    // Prepare Columns for the Visual Grid (2 ports per column)
    const portColumns: Array<InterfaceData[]> = [];
    for (let i = 0; i < portsData.length; i += 2) {
        portColumns.push([portsData[i], portsData[i + 1]].filter(Boolean));
    }

    // Format Bytes
    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (isLoading) {
        return <div className="p-8 text-center text-gray-400 animate-pulse">Loading interface data...</div>;
    }

    if (portsData.length === 0) {
        return (
            <div className="p-8 flex flex-col items-center justify-center text-gray-400 bg-gray-800 rounded-lg border border-gray-700">
                <AlertCircle className="w-12 h-12 mb-2 text-gray-600" />
                <p>No network interfaces found for this device.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">

            {/* Top Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex flex-col">
                    <span className="text-gray-400 text-xs uppercase tracking-wider">Interfaces</span>
                    <div className="mt-1 flex items-end justify-between">
                        <span className="text-2xl font-bold text-white">{portsData.length}</span>
                        <Activity className="w-5 h-5 text-blue-500 mb-1" />
                    </div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex flex-col">
                    <span className="text-gray-400 text-xs uppercase tracking-wider">Status</span>
                    <div className="mt-1 flex items-center gap-3 text-sm font-medium">
                        <span className="text-green-400 flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div> {upCount} Up
                        </span>
                        <span className="text-red-400 flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div> {downCount} Down
                        </span>
                    </div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex flex-col">
                    <span className="text-gray-400 text-xs uppercase tracking-wider">Total Inbound</span>
                    <div className="mt-1 flex items-end justify-between">
                        <span className="text-xl font-bold text-white">{formatBytes(totalOctetsIn)}</span>
                        <ArrowDown className="w-5 h-5 text-green-500 mb-1" />
                    </div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex flex-col">
                    <span className="text-gray-400 text-xs uppercase tracking-wider">Total Outbound</span>
                    <div className="mt-1 flex items-end justify-between">
                        <span className="text-xl font-bold text-white">{formatBytes(totalOctetsOut)}</span>
                        <ArrowUp className="w-5 h-5 text-orange-500 mb-1" />
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <EthernetPort className="w-5 h-5 text-orange-500" />
                        Physical Layout
                    </h3>
                    <span className="text-xs text-gray-500">Click a port to highlight</span>
                </div>

                {/* Visual Grid */}
                <div className="p-6 bg-gray-900 flex justify-center overflow-x-auto">
                    <div className="inline-flex gap-2 flex-wrap bg-gray-800/50 p-6 rounded-xl border border-gray-700 shadow-inner">
                        {portColumns.map((column, colIndex) => (
                            <div key={colIndex} className="flex flex-col gap-2">
                                {column.map((port, portIndex) => (
                                    <div
                                        key={port.id}
                                        onClick={() => setSelectedPort(port.index === selectedPort ? null : port.index)}
                                        title={`${port.description} (${port.status})`}
                                        className={`
                                            group relative flex flex-col items-center justify-center 
                                            p-2 rounded-md cursor-pointer transition-all duration-200
                                            ${selectedPort === port.index
                                                ? 'bg-blue-900/40 ring-2 ring-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]'
                                                : 'bg-gray-900 hover:bg-gray-700'}
                                        `}
                                    >
                                        {/* Top Index Label (for first row) */}
                                        {portIndex === 0 && (
                                            <span className={`text-[10px] font-mono mb-1 ${selectedPort === port.index ? 'text-blue-300' : 'text-gray-500'}`}>
                                                {port.index}
                                            </span>
                                        )}

                                        <EthernetPort
                                            size={36}
                                            strokeWidth={1.5}
                                            // Rotate the bottom row ports for that "Switch" look
                                            className={`
                                                ${portIndex === 1 ? "rotate-180" : ""} 
                                                transition-colors duration-300
                                            `}
                                            color={
                                                port.status === "up"
                                                    ? "rgb(34, 197, 94)" // Green
                                                    : "rgb(75, 85, 99)"  // Gray (using gray for down instead of red usually looks cleaner on switches, but changed to red below if preferred)
                                            }
                                            fill={
                                                port.status === "up"
                                                    ? "rgba(34, 197, 94, 0.2)"
                                                    : "transparent"
                                            }
                                        />

                                        {/* Status Dot */}
                                        <div className={`absolute ${portIndex === 0 ? 'top-1 right-1' : 'bottom-1 right-1'} w-1.5 h-1.5 rounded-full ${port.status === 'up' ? 'bg-green-500 shadow-[0_0_5px_#22c55e]' : 'bg-red-500'}`}></div>

                                        {/* Bottom Index Label (for second row) */}
                                        {portIndex === 1 && (
                                            <span className={`text-[10px] font-mono mt-1 ${selectedPort === port.index ? 'text-blue-300' : 'text-gray-500'}`}>
                                                {port.index}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Detailed Table */}
                <div className="border-t border-gray-700">
                    <div className="px-4 py-3 bg-gray-800 flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-300">Interface Details</h4>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs text-left">
                            <thead>
                                <tr className="bg-gray-800/50 border-b border-gray-700 text-gray-400 uppercase tracking-wider">
                                    <th className="px-4 py-3 font-medium">Idx</th>
                                    <th className="px-4 py-3 font-medium">Description</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                    <th className="px-4 py-3 font-medium">Speed</th>
                                    <th className="px-4 py-3 font-medium text-right">Traffic (In)</th>
                                    <th className="px-4 py-3 font-medium text-right">Traffic (Out)</th>
                                    <th className="px-4 py-3 font-medium text-right">Errors</th>
                                    <th className="px-4 py-3 font-medium font-mono">MAC Address</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700 bg-gray-900">
                                {portsData.map((port) => (
                                    <tr
                                        key={port.id}
                                        onClick={() => setSelectedPort(port.index)}
                                        className={`
                                            cursor-pointer transition-colors hover:bg-gray-800 
                                            ${selectedPort === port.index ? 'bg-blue-900/10 border-l-2 border-l-blue-500' : 'border-l-2 border-l-transparent'}
                                        `}
                                    >
                                        <td className="px-4 py-3 font-mono text-gray-500">{port.index}</td>
                                        <td className="px-4 py-3 font-medium text-white">
                                            {port.description || <span className="text-gray-600 italic">-</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${port.status === 'up'
                                                ? 'bg-green-900/20 text-green-400 border-green-900/30'
                                                : 'bg-red-900/20 text-red-400 border-red-900/30'
                                                }`}>
                                                {port.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-400">{port.speed || '-'}</td>
                                        <td className="px-4 py-3 text-right text-gray-300 font-mono">
                                            {formatBytes(port.in_octets || 0)}
                                        </td>
                                        <td className="px-4 py-3 text-right text-gray-300 font-mono">
                                            {formatBytes(port.out_octets || 0)}
                                        </td>
                                        <td className={`px-4 py-3 text-right font-mono ${(port.in_errors > 0 || port.out_errors > 0) ? 'text-red-400' : 'text-gray-500'
                                            }`}>
                                            {(port.in_errors || 0) + (port.out_errors || 0)}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-gray-500 text-[10px]">
                                            {port.mac_address || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SnmpInterface;
